/**
 * Contact Store
 * Zustand store for vendor contact state management
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ContactResponse } from '@/types/api/contact';

/**
 * Contact pagination state
 */
export interface ContactPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * Contact store state
 */
export interface ContactState {
  // Data - keyed by vendorId
  contactsByVendor: Record<number, ContactResponse[]>;
  currentContact: ContactResponse | null;

  // Pagination - keyed by vendorId
  paginationByVendor: Record<number, ContactPagination>;

  // UI State
  isLoading: boolean;
  error: string | null;

  // Modal state
  isContactModalOpen: boolean;
  editingContactId: number | null;
  currentVendorId: number | null;

  // Actions - Data
  setContactsForVendor: (vendorId: number, contacts: ContactResponse[]) => void;
  setCurrentContact: (contact: ContactResponse | null) => void;
  addContact: (vendorId: number, contact: ContactResponse) => void;
  updateContact: (vendorId: number, contactId: number, updates: Partial<ContactResponse>) => void;
  removeContact: (vendorId: number, contactId: number) => void;
  clearContactsForVendor: (vendorId: number) => void;

  // Actions - Pagination
  setPaginationForVendor: (vendorId: number, pagination: Partial<ContactPagination>) => void;

  // Actions - UI State
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Modal
  openContactModal: (vendorId: number, contactId?: number) => void;
  closeContactModal: () => void;

  // Actions - Reset
  reset: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  contactsByVendor: {},
  currentContact: null,
  paginationByVendor: {},
  isLoading: false,
  error: null,
  isContactModalOpen: false,
  editingContactId: null,
  currentVendorId: null,
};

/**
 * Default pagination
 */
const defaultPagination: ContactPagination = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
};

/**
 * Contact Zustand store
 */
export const useContactStore = create<ContactState>()(
  devtools(
    (set) => ({
      ...initialState,

      // Data actions
      setContactsForVendor: (vendorId, contacts) =>
        set(
          (state) => ({
            contactsByVendor: {
              ...state.contactsByVendor,
              [vendorId]: contacts,
            },
          }),
          false,
          'contacts/setContactsForVendor'
        ),

      setCurrentContact: (contact) =>
        set({ currentContact: contact }, false, 'contacts/setCurrentContact'),

      addContact: (vendorId, contact) =>
        set(
          (state) => ({
            contactsByVendor: {
              ...state.contactsByVendor,
              [vendorId]: [
                contact,
                ...(state.contactsByVendor[vendorId] || []),
              ],
            },
          }),
          false,
          'contacts/addContact'
        ),

      updateContact: (vendorId, contactId, updates) =>
        set(
          (state) => ({
            contactsByVendor: {
              ...state.contactsByVendor,
              [vendorId]: (state.contactsByVendor[vendorId] || []).map((c) =>
                c.contactId === contactId ? { ...c, ...updates } : c
              ),
            },
          }),
          false,
          'contacts/updateContact'
        ),

      removeContact: (vendorId, contactId) =>
        set(
          (state) => ({
            contactsByVendor: {
              ...state.contactsByVendor,
              [vendorId]: (state.contactsByVendor[vendorId] || []).filter(
                (c) => c.contactId !== contactId
              ),
            },
          }),
          false,
          'contacts/removeContact'
        ),

      clearContactsForVendor: (vendorId) =>
        set(
          (state) => {
            const { [vendorId]: _, ...rest } = state.contactsByVendor;
            return { contactsByVendor: rest };
          },
          false,
          'contacts/clearContactsForVendor'
        ),

      // Pagination actions
      setPaginationForVendor: (vendorId, pagination) =>
        set(
          (state) => ({
            paginationByVendor: {
              ...state.paginationByVendor,
              [vendorId]: {
                ...(state.paginationByVendor[vendorId] || defaultPagination),
                ...pagination,
              },
            },
          }),
          false,
          'contacts/setPaginationForVendor'
        ),

      // UI state actions
      setLoading: (isLoading) =>
        set({ isLoading }, false, 'contacts/setLoading'),

      setError: (error) =>
        set({ error }, false, 'contacts/setError'),

      // Modal actions
      openContactModal: (vendorId, contactId) =>
        set(
          {
            isContactModalOpen: true,
            currentVendorId: vendorId,
            editingContactId: contactId ?? null,
          },
          false,
          'contacts/openContactModal'
        ),

      closeContactModal: () =>
        set(
          {
            isContactModalOpen: false,
            currentVendorId: null,
            editingContactId: null,
            currentContact: null,
          },
          false,
          'contacts/closeContactModal'
        ),

      // Reset action
      reset: () =>
        set(initialState, false, 'contacts/reset'),
    }),
    { name: 'ContactStore' }
  )
);

/**
 * Selectors for common queries
 */

// Stable empty array to prevent infinite re-renders
const EMPTY_CONTACTS: ContactResponse[] = [];

// Get contacts for a specific vendor
export const useContactsForVendor = (vendorId: number) => {
  return useContactStore((state) => state.contactsByVendor[vendorId] || EMPTY_CONTACTS);
};

// Get pagination for a specific vendor
export const usePaginationForVendor = (vendorId: number) => {
  return useContactStore(
    (state) => state.paginationByVendor[vendorId] || defaultPagination
  );
};

// Get primary contact for a vendor
export const usePrimaryContact = (vendorId: number) => {
  return useContactStore((state) =>
    (state.contactsByVendor[vendorId] || EMPTY_CONTACTS).find((c) => c.isPrimary)
  );
};

// Get active contacts count for a vendor
export const useActiveContactsCount = (vendorId: number) => {
  return useContactStore((state) =>
    (state.contactsByVendor[vendorId] || []).filter((c) => c.isActive).length
  );
};
